/**
 * 006 · T036 · `useInstallPrompt` (research R-22): expone el `beforeinstallprompt` capturado
 * (Chromium en Android) para ofrecer un botón "Instalar" en lugar de las instrucciones manuales, y
 * `appinstalled` para confirmar la instalación.
 *
 * Los eventos los escucha `prompt-store.ts` desde que se carga el módulo (antes del primer
 * render); aquí solo se suscribe React con `useSyncExternalStore`.
 */
import { useSyncExternalStore } from 'react';

import { installPromptStore, promptInstall, type InstallOutcome } from './prompt-store';

export interface UseInstallPromptResult {
  /** `true` cuando el navegador ofreció el prompt nativo (Chromium en Android). */
  canPrompt: boolean;
  /** `true` tras el evento `appinstalled`. */
  installed: boolean;
  /** Dispara el prompt nativo; `unavailable` si no se capturó ninguno. */
  prompt: () => Promise<InstallOutcome>;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const deferred = useSyncExternalStore(
    installPromptStore.subscribe,
    installPromptStore.getPrompt,
    () => null,
  );
  const installed = useSyncExternalStore(
    installPromptStore.subscribe,
    installPromptStore.wasInstalled,
    () => false,
  );
  return { canPrompt: deferred !== null, installed, prompt: promptInstall };
}
